// src/demoData.js
// The public demo business: a small remodeling company, entirely made up.
//
// Pure and deterministic. No Math.random, no Date.now, no database, no React. Give buildDemoData the same
// "today" and you get the same data back. Every date is built from today with the local calendar (never
// toISOString), so "This month", "This quarter", "This year", "Last year" and the monthly trend all have
// data whatever day the demo is opened.
//
// The shapes are exactly the ones the live screens already read (see useContractorData in src/App.js):
// job summaries, inbox items, vendor rules, saved estimates. Money is worked out in whole cents and
// given to the screens in dollars, the same units the live path uses.
//
//   revenue      = invoices + manual revenue
//   materialCost = purchase lines on the job + manual expenses that do not count as labor (laborRules)
//   laborCost    = labor entries + manual expenses that count as labor
//   costs        = materialCost + laborCost
//   profit       = revenue - costs
//   costByVendor = purchases + manual expenses by vendor (labor entries have no vendor)
//
// Overhead items (rent, insurance) are never part of a job's costs.

import { toWholeCents, formatMoney } from "./money";
import { classifyExpense } from "./laborRules";
import { localYMD } from "./dateRange";
import { marginPercent, monthlyTotalsForAI, jobEvents } from "./aiCharts";
import { jobCompleteness } from "./jobCompleteness";
import { AI_SUGGESTIONS } from "./aiGreeting";

export const DEMO_BUSINESS_NAME = "Demo business";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// ─── Dates ───────────────────────────────────────────────────────────────────

// "YYYY-MM-DD" for a day in the month that is `monthsAgo` months before today's month. The day is capped to
// the length of that month, and in the current month to today, so nothing is ever dated in the future.
export function demoDate(today, monthsAgo, day) {
  const first = new Date(today.getFullYear(), today.getMonth() - monthsAgo, 1);
  const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  let d = Math.min(day, daysInMonth);
  if (monthsAgo === 0) d = Math.min(d, today.getDate());
  return localYMD(new Date(first.getFullYear(), first.getMonth(), d));
}

// "YYYY-MM-DD" text -> a local Date (no time zone shift).
function parseYMD(s) {
  const [y, m, d] = String(s).split("-").map(Number);
  return new Date(y, m - 1, d);
}

// ─── The business, as plain tables ───────────────────────────────────────────
// Rows are [months ago, day, ...]. Amounts are dollars.
//   inv   [mAgo, day, amount, description, balance still owed]
//   pur   [vendor, mAgo, day, amount, description, payment type]
//   lab   [worker, mAgo, day, hours, hourly rate, description]
//   mexp  [vendor, mAgo, day, amount, description, category]
//   mrev  [mAgo, day, amount, description, payment method]

const JOB_SPECS = [
  {
    key: "patel-kitchen", name: "Patel kitchen remodel", client: "Patel Family", type: "Remodel", status: "Complete",
    inv: [[14, 5, 15000, "Deposit", 0], [13, 10, 16000, "Progress payment", 0], [12, 8, 15000, "Final payment", 0]],
    pur: [
      ["Ridgeline Cabinet Works", 14, 14, 9800, "Custom maple cabinets", "Check"],
      ["Fernhill Lumber & Supply", 14, 20, 1320, "Framing and blocking", "CreditCard"],
      ["Granite Peak Countertops", 13, 6, 4200, "Quartz countertops", "Check"],
      ["Northgate Flooring Wholesale", 13, 14, 1750, "Tile flooring", "CreditCard"],
      ["Bluebird Electric", 13, 18, 2400, "Electrical rough-in and finish", "Check"],
      ["Tidewater Plumbing Co", 13, 20, 1600, "Sink and dishwasher plumbing", "Check"],
      ["Lantern Lighting & Fixtures", 12, 2, 1100, "Pendants and under-cabinet lights", "CreditCard"],
      ["Maple & Main Paint Supply", 12, 4, 640, "Paint and primer", "CreditCard"],
    ],
    lab: [
      ["Marcus Reed", 14, 25, 64, 42, "Demo and framing"], ["Marcus Reed", 13, 5, 64, 42, "Cabinet install"],
      ["Dana Ortiz", 13, 15, 56, 45, "Trim and finish carpentry"], ["Dana Ortiz", 12, 3, 32, 45, "Punch list"],
    ],
  },
  {
    key: "okafor-bath", name: "Okafor primary bath", client: "Okafor Family", type: "Remodel", status: "Complete",
    inv: [[13, 3, 8000, "Deposit", 0], [12, 6, 9000, "Progress payment", 0], [11, 12, 7500, "Final payment", 0]],
    pur: [
      ["Fernhill Lumber & Supply", 13, 12, 780, "Framing", "CreditCard"],
      ["Cobblestone Tile & Stone", 12, 1, 2950, "Floor and shower tile", "Check"],
      ["Tidewater Plumbing Co", 12, 5, 2300, "Shower valve and fixtures", "Check"],
      ["Lantern Lighting & Fixtures", 12, 9, 890, "Vanity lights and fan", "CreditCard"],
      ["Keystone Drywall Pros", 12, 12, 1450, "Drywall and finish", "Check"],
      ["Granite Peak Countertops", 11, 3, 1320, "Vanity top", "Check"],
      ["Maple & Main Paint Supply", 11, 8, 410, "Paint", "CreditCard"],
    ],
    lab: [
      ["Marcus Reed", 13, 20, 40, 42, "Demo and framing"], ["Marcus Reed", 12, 10, 56, 42, "Tile setting"],
      ["Dana Ortiz", 11, 5, 40, 45, "Trim and door"],
    ],
    mexp: [["Cedar County Permits", 13, 6, 360, "Building permit", "permits"]],
  },
  {
    key: "lindqvist-deck", name: "Lindqvist cedar deck", client: "Lindqvist Household", type: "Exterior", status: "Complete",
    inv: [[12, 15, 6000, "Deposit", 0], [11, 10, 9200, "Final invoice", 0]],
    mrev: [[10, 5, 3000, "Final payment, cash", "cash"]],
    pur: [
      ["Evergreen Deck Supply", 12, 18, 5950, "Cedar decking and rail", "Check"],
      ["Sawbuck Rentals", 12, 17, 280, "Post hole auger", "CreditCard"],
      ["Fernhill Lumber & Supply", 12, 20, 1380, "Posts, joists and concrete", "CreditCard"],
    ],
    lab: [
      ["Chris Doyle", 12, 22, 60, 40, "Footings and framing"], ["Chris Doyle", 11, 4, 60, 40, "Decking and rail"],
      ["Marcus Reed", 11, 6, 24, 42, "Stairs"],
    ],
    mexp: [["Cedar County Permits", 12, 16, 185, "Deck permit", "permits"]],
  },
  {
    key: "dental-fitout", name: "Brightwater dental office fit-out", client: "Brightwater Dental", type: "Commercial", status: "Complete",
    inv: [[12, 2, 28200, "Deposit", 0], [11, 1, 23500, "Progress payment 2", 0], [10, 1, 23500, "Progress payment 3", 0], [9, 1, 18800, "Final payment", 0]],
    pur: [
      ["Sawbuck Rentals", 12, 6, 1150, "Dumpster and lift rental", "CreditCard"],
      ["Fernhill Lumber & Supply", 12, 10, 3300, "Metal stud and framing", "Check"],
      ["Keystone Drywall Pros", 11, 6, 6900, "Drywall and finishing", "Check"],
      ["Bluebird Electric", 11, 12, 10400, "Electrical for exam rooms", "Check"],
      ["Tidewater Plumbing Co", 11, 14, 5200, "Exam room plumbing", "Check"],
      ["Copperline HVAC", 10, 5, 9600, "Ductwork and units", "Check"],
      ["Lantern Lighting & Fixtures", 10, 9, 3100, "Lighting package", "Check"],
      ["Granite Peak Countertops", 10, 18, 3200, "Reception counter", "Check"],
      ["Northgate Flooring Wholesale", 9, 6, 4600, "Vinyl plank flooring", "Check"],
      ["Cobblestone Tile & Stone", 9, 8, 1900, "Accent wall tile", "CreditCard"],
      ["Maple & Main Paint Supply", 9, 12, 1180, "Paint", "CreditCard"],
    ],
    lab: [
      ["Marcus Reed", 12, 15, 100, 42, "Layout and framing"], ["Marcus Reed", 11, 10, 100, 42, "Rough-in support"],
      ["Marcus Reed", 10, 12, 64, 42, "Finish work"], ["Marcus Reed", 9, 5, 48, 42, "Punch list"],
      ["Chris Doyle", 12, 20, 80, 40, "Demo and framing"], ["Chris Doyle", 11, 20, 64, 40, "Ceiling grid"],
      ["Dana Ortiz", 10, 20, 80, 45, "Cabinets and trim"], ["Dana Ortiz", 9, 10, 48, 45, "Doors and hardware"],
    ],
    mexp: [["Cedar County Permits", 12, 4, 1850, "Commercial permit and inspections", "permits"]],
  },
  {
    key: "reyes-basement", name: "Reyes basement finish", client: "Reyes Family", type: "Remodel", status: "Complete",
    inv: [[10, 4, 17000, "Deposit", 0], [9, 6, 17000, "Progress payment", 0], [8, 3, 18000, "Final payment", 0]],
    pur: [
      ["Fernhill Lumber & Supply", 10, 10, 5000, "Framing lumber", "Check"],
      ["Keystone Drywall Pros", 9, 2, 3950, "Drywall and finishing", "Check"],
      ["Bluebird Electric", 9, 9, 3600, "Electrical", "Check"],
      ["Tidewater Plumbing Co", 9, 12, 2700, "Bathroom rough-in", "Check"],
      ["Northgate Flooring Wholesale", 9, 14, 4300, "Luxury vinyl flooring", "Check"],
      ["Copperline HVAC", 8, 1, 2200, "Heat and vents", "Check"],
      ["Lantern Lighting & Fixtures", 8, 4, 1150, "Recessed lights", "CreditCard"],
      ["Cobblestone Tile & Stone", 8, 5, 1250, "Bathroom tile", "CreditCard"],
      ["Maple & Main Paint Supply", 8, 8, 780, "Paint", "CreditCard"],
      ["Slate & Setting Supply", 9, 20, 360, "Setting materials", "CreditCard"],
      ["Slate & Setting Supply", 8, 3, 220, "Thinset and spacers", "CreditCard"],
    ],
    lab: [
      ["Marcus Reed", 10, 15, 96, 42, "Framing"], ["Marcus Reed", 9, 10, 80, 42, "Install"], ["Marcus Reed", 8, 2, 40, 42, "Punch list"],
      ["Dana Ortiz", 9, 20, 80, 45, "Trim and doors"], ["Dana Ortiz", 8, 6, 56, 45, "Finish carpentry"],
      ["Chris Doyle", 10, 18, 40, 40, "Demo"],
    ],
    mexp: [["Cedar County Permits", 10, 6, 540, "Building permit", "permits"]],
  },
  {
    key: "greene-kitchen", name: "Greene kitchen and pantry", client: "Greene Family", type: "Remodel", status: "Complete",
    inv: [[9, 10, 20000, "Deposit", 0], [8, 5, 21500, "Progress payment", 0], [7, 9, 20000, "Final payment", 0]],
    pur: [
      ["Ridgeline Cabinet Works", 9, 15, 12400, "Cabinets and pantry shelving", "Check"],
      ["Fernhill Lumber & Supply", 9, 18, 2100, "Framing and blocking", "CreditCard"],
      ["Granite Peak Countertops", 8, 2, 5900, "Countertops and backsplash", "Check"],
      ["Keystone Drywall Pros", 8, 6, 1900, "Drywall", "Check"],
      ["Northgate Flooring Wholesale", 8, 10, 2300, "Flooring", "Check"],
      ["Bluebird Electric", 8, 12, 3300, "Electrical", "Check"],
      ["Tidewater Plumbing Co", 8, 14, 2100, "Plumbing", "Check"],
      ["Lantern Lighting & Fixtures", 7, 2, 1600, "Lighting", "CreditCard"],
      ["Maple & Main Paint Supply", 7, 4, 920, "Paint", "CreditCard"],
    ],
    lab: [
      ["Marcus Reed", 9, 22, 96, 42, "Demo and framing"], ["Marcus Reed", 8, 7, 80, 42, "Cabinet install"], ["Marcus Reed", 7, 3, 40, 42, "Punch list"],
      ["Dana Ortiz", 8, 9, 120, 45, "Finish carpentry"], ["Dana Ortiz", 7, 6, 40, 45, "Trim"],
      ["Chris Doyle", 9, 24, 40, 40, "Demo"],
    ],
  },
  {
    key: "delgado-bath", name: "Delgado hall bath", client: "Delgado Household", type: "Remodel", status: "Complete",
    inv: [[8, 8, 7400, "Deposit", 0], [7, 2, 7400, "Final payment", 0]],
    pur: [
      ["Fernhill Lumber & Supply", 8, 10, 780, "Framing and subfloor", "CreditCard"],
      ["Cobblestone Tile & Stone", 8, 12, 2900, "Tile, re-ordered after damage", "Check"],
      ["Tidewater Plumbing Co", 8, 15, 2650, "Plumbing and leak repair", "Check"],
      ["Keystone Drywall Pros", 7, 1, 1350, "Drywall, second pass", "Check"],
      ["Lantern Lighting & Fixtures", 7, 3, 640, "Fixtures", "CreditCard"],
      ["Maple & Main Paint Supply", 7, 5, 390, "Paint", "CreditCard"],
    ],
    lab: [
      ["Marcus Reed", 8, 14, 56, 42, "Demo and repair"], ["Marcus Reed", 7, 1, 48, 42, "Tile and finish"],
      ["Chris Doyle", 8, 18, 64, 40, "Subfloor repair"], ["Dana Ortiz", 7, 2, 40, 45, "Trim"],
    ],
  },
  {
    key: "patel-guest-bath", name: "Patel guest bath", client: "Patel Family", type: "Remodel", status: "Complete",
    inv: [[7, 6, 6600, "Deposit", 0], [6, 3, 6600, "Progress payment", 0], [5, 10, 6600, "Final payment", 0]],
    pur: [
      ["Cobblestone Tile & Stone", 7, 10, 2350, "Tile", "Check"],
      ["Fernhill Lumber & Supply", 7, 12, 620, "Framing", "CreditCard"],
      ["Tidewater Plumbing Co", 6, 1, 1950, "Plumbing", "Check"],
      ["Keystone Drywall Pros", 6, 2, 1100, "Drywall", "Check"],
      ["Granite Peak Countertops", 6, 6, 980, "Vanity top", "Check"],
      ["Lantern Lighting & Fixtures", 6, 8, 540, "Lighting", "CreditCard"],
      ["Maple & Main Paint Supply", 5, 2, 330, "Paint", "CreditCard"],
    ],
    lab: [
      ["Marcus Reed", 7, 14, 48, 42, "Demo and framing"], ["Marcus Reed", 6, 10, 40, 42, "Tile setting"],
      ["Dana Ortiz", 6, 14, 40, 45, "Trim and doors"], ["Dana Ortiz", 5, 6, 16, 45, "Punch list"],
    ],
  },
  {
    key: "whitfield-deck", name: "Whitfield composite deck", client: "Whitfield Household", type: "Exterior", status: "Complete",
    inv: [[6, 4, 12000, "Deposit", 0], [5, 6, 9000, "Progress payment", 0], [4, 3, 6400, "Final invoice", 3200]],
    pur: [
      ["Evergreen Deck Supply", 6, 8, 9000, "Composite decking and rail", "Check"],
      ["Sawbuck Rentals", 6, 9, 320, "Post hole auger", "CreditCard"],
      ["Fernhill Lumber & Supply", 6, 10, 2150, "Framing and concrete", "CreditCard"],
      ["Lantern Lighting & Fixtures", 5, 3, 480, "Deck lights", "CreditCard"],
      ["Timberline Fasteners", 6, 12, 300, "Deck screws and brackets", "CreditCard"],
      ["Timberline Fasteners", 5, 3, 250, "Joist hangers and hardware", "CreditCard"],
    ],
    lab: [
      ["Chris Doyle", 6, 15, 80, 40, "Footings and framing"], ["Chris Doyle", 5, 5, 72, 40, "Decking"],
      ["Marcus Reed", 5, 10, 48, 42, "Rail and stairs"], ["Marcus Reed", 4, 2, 24, 42, "Punch list"],
    ],
    mexp: [["Cedar County Permits", 6, 5, 220, "Deck permit", "permits"]],
  },
  {
    // Costs and revenue, but no labor recorded: the margin looks far better than the job really was.
    key: "alder-cafe", name: "Alder Street Cafe counter and floors", client: "Alder Street Cafe", type: "Commercial", status: "Complete",
    inv: [[5, 12, 11000, "Deposit", 0], [4, 10, 11000, "Final payment", 0]],
    pur: [
      ["Granite Peak Countertops", 5, 15, 4800, "Service counter", "Check"],
      ["Northgate Flooring Wholesale", 5, 18, 3900, "Commercial flooring", "Check"],
      ["Ridgeline Cabinet Works", 5, 20, 2300, "Back-bar cabinets", "Check"],
      ["Maple & Main Paint Supply", 4, 2, 520, "Paint", "CreditCard"],
      ["Lantern Lighting & Fixtures", 4, 5, 910, "Lighting", "CreditCard"],
    ],
  },
  {
    key: "nguyen-floors", name: "Nguyen whole-house floors", client: "Nguyen Household", type: "Flooring", status: "Complete",
    inv: [[5, 18, 10400, "Deposit", 0], [3, 14, 10400, "Progress payment", 0], [2, 4, 10400, "Final payment", 0]],
    pur: [
      ["Northgate Flooring Wholesale", 5, 20, 10600, "Engineered oak flooring", "Check"],
      ["Fernhill Lumber & Supply", 4, 8, 1950, "Trim and baseboard", "CreditCard"],
      ["Sawbuck Rentals", 4, 14, 410, "Floor sander rental", "CreditCard"],
      ["Cobblestone Tile & Stone", 3, 10, 1100, "Entry tile", "CreditCard"],
      ["Maple & Main Paint Supply", 2, 1, 840, "Baseboard paint", "CreditCard"],
    ],
    lab: [
      ["Dana Ortiz", 4, 10, 80, 45, "Floor install"], ["Dana Ortiz", 3, 5, 80, 45, "Floor install"],
      ["Chris Doyle", 3, 20, 24, 40, "Trim"], ["Chris Doyle", 2, 3, 24, 40, "Finish"],
    ],
  },
  {
    // Costs so far, nothing invoiced yet.
    key: "okafor-mudroom", name: "Okafor mudroom addition", client: "Okafor Family", type: "Addition", status: "In Progress",
    inv: [],
    pur: [
      ["Fernhill Lumber & Supply", 2, 18, 2000, "Framing lumber", "Check"],
      ["Fernhill Lumber & Supply", 2, 22, 450, "Sill plates and joists", "CreditCard"],
      ["Fernhill Lumber & Supply", 1, 4, 380, "Sheathing and trim lumber", "CreditCard"],
      ["Bluebird Electric", 2, 23, 320, "Electrical service upgrade", "Check"],
      ["Bluebird Electric", 1, 8, 260, "Outlets and lighting circuits", "Check"],
      ["Keystone Drywall Pros", 1, 10, 700, "Insulation and drywall", "Check"],
      ["Keystone Drywall Pros", 1, 14, 180, "Drywall materials", "Check"],
      ["Ridgeline Cabinet Works", 0, 3, 900, "Built-in bench and lockers", "Check"],
    ],
    lab: [
      ["Marcus Reed", 2, 21, 16, 42, "Foundation and framing"], ["Marcus Reed", 1, 6, 24, 42, "Framing and sheathing"],
      ["Chris Doyle", 1, 18, 16, 40, "Roofing and siding"], ["Marcus Reed", 0, 2, 16, 42, "Windows and door"],
    ],
    mexp: [["Cedar County Permits", 2, 19, 640, "Building permit", "permits"]],
  },
  {
    key: "lindqvist-porch", name: "Lindqvist porch repair", client: "Lindqvist Household", type: "Exterior", status: "Complete",
    inv: [[1, 3, 2800, "Deposit", 0], [1, 24, 2400, "Final payment", 0]],
    pur: [
      ["Evergreen Deck Supply", 1, 5, 1150, "Cedar boards and rail", "Check"],
      ["Fernhill Lumber & Supply", 1, 6, 420, "Posts", "CreditCard"],
    ],
    lab: [["Chris Doyle", 1, 9, 32, 40, "Rot repair"], ["Chris Doyle", 1, 20, 24, 40, "Rail and stain"]],
  },
  {
    // Two expenses in the review list point at this job.
    key: "greene-bath", name: "Greene primary bath", client: "Greene Family", type: "Remodel", status: "In Progress",
    inv: [[1, 8, 9000, "Deposit", 0], [1, 27, 9000, "Progress payment", 9000]],
    pur: [
      ["Cobblestone Tile & Stone", 1, 12, 3050, "Floor and shower tile", "Check"],
      ["Cobblestone Tile & Stone", 1, 19, 460, "Niche and trim tile", "CreditCard"],
      ["Tidewater Plumbing Co", 1, 16, 2200, "Rough-in", "Check"],
      ["Tidewater Plumbing Co", 1, 9, 640, "Drain line rework", "Check"],
      ["Keystone Drywall Pros", 1, 22, 1250, "Drywall", "Check"],
      ["Lantern Lighting & Fixtures", 0, 2, 780, "Vanity lights", "CreditCard"],
    ],
    lab: [
      ["Marcus Reed", 1, 14, 40, 42, "Demo"], ["Marcus Reed", 1, 25, 40, 42, "Tile setting"],
      ["Dana Ortiz", 0, 2, 24, 45, "Vanity install"],
    ],
  },
  {
    key: "nguyen-kitchen", name: "Nguyen kitchen refresh", client: "Nguyen Household", type: "Remodel", status: "In Progress",
    inv: [[0, 2, 11500, "Deposit", 11500]],
    pur: [
      ["Ridgeline Cabinet Works", 0, 2, 4300, "Cabinet deposit", "Check"],
      ["Granite Peak Countertops", 0, 2, 1900, "Countertop deposit", "Check"],
    ],
    lab: [["Dana Ortiz", 0, 2, 16, 45, "Measure and demo"], ["Marcus Reed", 0, 2, 16, 42, "Demo"]],
  },
];

// Expenses the owner confirmed by hand and expenses Canopy matched on its own. Both are real costs on the job,
// so they are added to the job's purchases (the live path does the same with a transaction row).
// [inbox id, job key, vendor, mAgo, day, amount, description, payment type, confidence, reason]
const AUTO_MATCHED = [
  ["demo-inbox-a1", "whitfield-deck", "Timberline Fasteners", 5, 12, 1240, "Hidden fasteners and trim", "Check", 0.95,
    "2 of 2 Timberline Fasteners expenses went to this job and the date fits the job's dates"],
  ["demo-inbox-a2", "reyes-basement", "Slate & Setting Supply", 8, 9, 1085, "Grout and tile trim", "CreditCard", 0.95,
    "2 of 2 Slate & Setting Supply expenses went to this job and the date fits the job's dates"],
];
const TAGGED = [
  ["demo-inbox-t1", "greene-kitchen", "Northgate Flooring Wholesale", 8, 11, 645, "Underlayment for the pantry floor", "Check"],
  ["demo-inbox-t2", "nguyen-floors", "Sawbuck Rentals", 4, 9, 190, "Extra day on the floor sander", "CreditCard"],
];

// Expenses with no job yet. suggestedJob is a job key (or null).
// [id, vendor, mAgo, day, amount, description, payment type, job key, confidence, reason]
const REVIEW_SUGGESTED = [
  // Confidence and reason are what the real matcher (api/_matching.js scoreExpense) gives for the vendor history
  // in the jobs above; src/demoData.test.js re-runs the matcher to prove it.
  ["demo-inbox-s1", "Fernhill Lumber & Supply", 2, 26, 1284.5, "Framing lumber for the mudroom", "Check", "okafor-mudroom", 0.59,
    "3 of 4 Fernhill Lumber & Supply expenses went to this job (only jobs active on this date)"],
  ["demo-inbox-s2", "Bluebird Electric", 2, 27, 1150, "Electrical rough-in, new circuits", "Check", "okafor-mudroom", 0.79,
    "2 of 2 Bluebird Electric expenses went to this job (only jobs active on this date)"],
  ["demo-inbox-s3", "Keystone Drywall Pros", 1, 21, 890, "Drywall for the mudroom", "Check", "okafor-mudroom", 0.57,
    "2 of 3 Keystone Drywall Pros expenses went to this job (only jobs active on this date)"],
  ["demo-inbox-s4", "Cobblestone Tile & Stone", 1, 28, 1420, "Shower tile and grout", "CreditCard", "greene-bath", 0.79,
    "2 of 2 Cobblestone Tile & Stone expenses went to this job (only jobs active on this date)"],
  ["demo-inbox-s5", "Tidewater Plumbing Co", 0, 2, 560, "Shower valve and drain", "Check", "greene-bath", 0.79,
    "2 of 2 Tidewater Plumbing Co expenses went to this job (only jobs active on this date)"],
];
const REVIEW_PENDING = [
  ["demo-inbox-p1", "Fernhill Lumber & Supply", 1, 15, 2340, "Lumber order, no job on the receipt", "Check"],
  ["demo-inbox-p2", "Northgate Flooring Wholesale", 2, 10, 1075, "Sample boxes and trim stock", "CreditCard"],
  ["demo-inbox-p3", "Ridgeline Cabinet Works", 1, 5, 650, "Replacement hinges and pulls", "Check"],
  ["demo-inbox-p4", "Roadside Fuel Card", 1, 30, 412.18, "Truck fuel", "CreditCard"],
  ["demo-inbox-p5", "Sawbuck Rentals", 0, 1, 245, "Ten-yard dumpster rental", "CreditCard"],
  ["demo-inbox-p6", "Maple & Main Paint Supply", 0, 2, 318.65, "Primer and caulk", "CreditCard"],
];
// [id, vendor, mAgo, day, amount, description, payment type]
const OVERHEAD_ITEMS = [
  ["demo-inbox-o1", "Oakmont Property Management", 2, 1, 2350, "Shop rent", "Check"],
  ["demo-inbox-o2", "Oakmont Property Management", 1, 1, 2350, "Shop rent", "Check"],
  ["demo-inbox-o3", "Oakmont Property Management", 0, 1, 2350, "Shop rent", "Check"],
  ["demo-inbox-o4", "Harborline Insurance", 4, 10, 1860, "General liability insurance, quarterly", "Check"],
  ["demo-inbox-o5", "Harborline Insurance", 1, 10, 1860, "General liability insurance, quarterly", "Check"],
  ["demo-inbox-o6", "Brightline Phone & Internet", 0, 4, 189.99, "Office phone and internet", "CreditCard"],
];
const DISMISSED_ITEMS = [
  ["demo-inbox-x1", "Lantern Lighting & Fixtures", 2, 12, 186.4, "Duplicate card charge, refunded", "CreditCard"],
];

const TRACKED_VENDORS = [
  "Fernhill Lumber & Supply", "Ridgeline Cabinet Works", "Cobblestone Tile & Stone", "Granite Peak Countertops",
  "Northgate Flooring Wholesale", "Evergreen Deck Supply", "Tidewater Plumbing Co", "Bluebird Electric",
  "Keystone Drywall Pros", "Copperline HVAC", "Lantern Lighting & Fixtures", "Maple & Main Paint Supply", "Sawbuck Rentals",
  "Cedar County Permits", "Timberline Fasteners", "Slate & Setting Supply",
];
const OVERHEAD_VENDORS = ["Oakmont Property Management", "Harborline Insurance", "Brightline Phone & Internet"];

// ─── Building the job summaries ──────────────────────────────────────────────

const dollars = (cents) => cents / 100;
const cents = (n) => toWholeCents(n);
const pad = (n, w) => String(n).padStart(w, "0");

function buildJobs(today) {
  let nInv = 0, nPur = 0, nLab = 0, nExp = 0, nRev = 0;
  const date = (m, d) => demoDate(today, m, d);
  const byDate = (field) => (a, b) => (a[field] < b[field] ? -1 : a[field] > b[field] ? 1 : 0);

  return JOB_SPECS.map((spec, i) => {
    const id = `demo-job-${pad(i + 1, 2)}`;
    const ref = (amount, description) => ({ Amount: amount, Description: description, AccountBasedExpenseLineDetail: { CustomerRef: { value: id } } });
    const mkPurchase = (pid, doc, vendor, m, d, amount, description, pay) => ({
      Id: pid, DocNumber: doc, TxnDate: date(m, d), TotalAmt: amount, PaymentType: pay,
      EntityRef: { name: vendor }, Line: [ref(amount, description)],
    });

    const invoices = (spec.inv || []).map(([m, d, amount, description, balance]) => {
      nInv += 1;
      return {
        Id: `demo-inv-${pad(nInv, 3)}`, DocNumber: `INV-${1000 + nInv}`, TxnDate: date(m, d),
        TotalAmt: amount, Balance: balance, Line: [{ Description: description, Amount: amount }],
        CustomerRef: { value: id },
      };
    }).sort(byDate("TxnDate"));

    const purchases = (spec.pur || []).map(([vendor, m, d, amount, description, pay]) => {
      nPur += 1;
      return mkPurchase(`demo-pur-${pad(nPur, 3)}`, `PO-${2000 + nPur}`, vendor, m, d, amount, description, pay);
    });
    AUTO_MATCHED.filter((a) => a[1] === spec.key).forEach(([iid, , vendor, m, d, amount, description, pay]) => {
      nPur += 1;
      purchases.push(mkPurchase(`demo-automatch-${iid}`, `PO-${2000 + nPur}`, vendor, m, d, amount, description, pay));
    });
    TAGGED.filter((t) => t[1] === spec.key).forEach(([iid, , vendor, m, d, amount, description, pay]) => {
      nPur += 1;
      purchases.push(mkPurchase(`demo-tagged-${iid}`, `PO-${2000 + nPur}`, vendor, m, d, amount, description, pay));
    });
    purchases.sort(byDate("TxnDate"));

    const laborEntries = (spec.lab || []).map(([worker, m, d, hours, rate, description]) => {
      nLab += 1;
      return {
        id: `demo-lab-${pad(nLab, 3)}`, jobId: id, description, workerName: worker, hours, hourlyRate: rate,
        amount: dollars(cents(hours * rate)), workDate: date(m, d), source: "manual",
      };
    }).sort(byDate("workDate"));

    const manualExpenses = (spec.mexp || []).map(([vendor, m, d, amount, description, category]) => {
      nExp += 1;
      return { id: `demo-exp-${pad(nExp, 3)}`, jobId: id, description, vendor, amount, category, expenseDate: date(m, d), source: "manual", isLabor: classifyExpense({ vendor, category }).isLabor };
    }).sort(byDate("expenseDate"));

    const manualRevenue = (spec.mrev || []).map(([m, d, amount, description, method]) => {
      nRev += 1;
      return { id: `demo-rev-${pad(nRev, 3)}`, jobId: id, description, amount, paymentMethod: method, revenueDate: date(m, d), source: "manual" };
    }).sort(byDate("revenueDate"));

    // All sums in whole cents.
    const sum = (list, pick) => list.reduce((s, x) => s + cents(pick(x)), 0);
    const invoiceC = sum(invoices, (x) => x.TotalAmt);
    const manualRevC = sum(manualRevenue, (x) => x.amount);
    const purchaseC = sum(purchases, (x) => x.Line[0].Amount);
    // A cash cost is labor or materials by the same rule as real data (keyword default; the demo has no owner choices).
    const manualExpLaborC = sum(manualExpenses.filter((x) => x.isLabor), (x) => x.amount);
    const manualExpC = sum(manualExpenses, (x) => x.amount) - manualExpLaborC;
    const laborC = sum(laborEntries, (x) => x.amount) + manualExpLaborC;
    const revenueC = invoiceC + manualRevC;
    const materialC = purchaseC + manualExpC;
    const costC = materialC + laborC;
    const profitC = revenueC - costC;

    const vendorC = {};
    purchases.forEach((p) => { vendorC[p.EntityRef.name] = (vendorC[p.EntityRef.name] || 0) + cents(p.Line[0].Amount); });
    manualExpenses.forEach((e) => { vendorC[e.vendor] = (vendorC[e.vendor] || 0) + cents(e.amount); });
    const costByVendor = {};
    Object.keys(vendorC).sort().forEach((v) => { costByVendor[v] = dollars(vendorC[v]); });

    const margin = marginPercent(profitC, revenueC);
    return {
      id, name: spec.name, clientName: spec.client, type: spec.type, status: spec.status,
      revenue: dollars(revenueC), costs: dollars(costC), materialCost: dollars(materialC), laborCost: dollars(laborC),
      profit: dollars(profitC), marginPct: margin === null ? "0.0" : margin.toFixed(1),
      outstanding: dollars(sum(invoices, (x) => x.Balance)),
      invoices, purchases, laborEntries, manualExpenses, manualRevenue, costByVendor,
      firstDate: invoices[0]?.TxnDate || "", lastDate: invoices[invoices.length - 1]?.TxnDate || "",
    };
  });
}

// ─── Inbox items, vendor rules, estimates ────────────────────────────────────

function buildInbox(today, jobIdOf) {
  const date = (m, d) => demoDate(today, m, d);
  let nDoc = 0;
  const item = (id, vendor, m, d, amount, description, paymentType, extra = {}) => ({
    id, docNumber: `PO-${3000 + ++nDoc}`,
    vendor, date: date(m, d), amount, description, paymentType,
    suggestedJob: null, suggestionReason: null, taggedJobId: null,
    confidence: null, matchTier: null, matchReason: null, matchedBy: null,
    ...extra.fields,
  });

  const autoMatched = AUTO_MATCHED.map(([id, jobKey, vendor, m, d, amount, desc, pay, conf, reason]) => item(id, vendor, m, d, amount, desc, pay, {
    fields: { suggestedJob: jobIdOf[jobKey], suggestionReason: reason, taggedJobId: jobIdOf[jobKey], confidence: conf, matchTier: "auto", matchReason: reason, matchedBy: "rule" },
  }));
  const tagged = TAGGED.map(([id, jobKey, vendor, m, d, amount, desc, pay]) => item(id, vendor, m, d, amount, desc, pay, {
    fields: { taggedJobId: jobIdOf[jobKey], matchedBy: "manual" },
  }));
  const suggested = REVIEW_SUGGESTED.map(([id, vendor, m, d, amount, desc, pay, jobKey, conf, reason]) => item(id, vendor, m, d, amount, desc, pay, {
    fields: { suggestedJob: jobIdOf[jobKey], suggestionReason: reason, confidence: conf, matchTier: "suggested", matchReason: reason, matchedBy: "rule" },
  }));
  const untagged = REVIEW_PENDING.map(([id, vendor, m, d, amount, desc, pay]) => item(id, vendor, m, d, amount, desc, pay, {
    fields: { matchTier: "needs_attention" },
  }));
  const overhead = OVERHEAD_ITEMS.map(([id, vendor, m, d, amount, desc, pay]) => item(id, vendor, m, d, amount, desc, pay, {
    fields: { matchReason: `Vendor rule: ${vendor} is a fixed cost`, matchedBy: "rule" },
  }));
  const dismissed = DISMISSED_ITEMS.map(([id, vendor, m, d, amount, desc, pay]) => item(id, vendor, m, d, amount, desc, pay));
  return { autoMatched, suggested, untagged, tagged, overhead, dismissed };
}

function buildVendorRules() {
  let n = 0;
  const rule = (vendor_name, rule_type) => { n += 1; return { id: `demo-rule-${pad(n, 2)}`, vendor_name, rule_type }; };
  return [...OVERHEAD_VENDORS.map((v) => rule(v, "overhead")), ...TRACKED_VENDORS.map((v) => rule(v, "tracked"))];
}

// Saved quotes and templates, in the shape of the estimates table (no linked_job_id on live).
// Lines: [description, category, amount]
function buildEstimates(today) {
  const stamp = (m, d) => `${demoDate(today, m, d)}T12:00:00.000Z`;
  const lines = (rows) => rows.map(([description, category, amount]) => ({ description, category, amount, mode: "flat", unitCost: "", quantity: "" }));
  const est = (id, name, job_type, expected_revenue, rows, notes, is_template, updated_at) => ({
    id, name, job_type, expected_revenue, cost_lines: lines(rows), notes, is_template, updated_at,
  });
  const estimates = [
    est("demo-est-1", "Bathroom remodel for the Chen family", "Remodel", 23500, [
      ["Tile and stone", "Materials", 3000], ["Plumbing", "Subcontractor", 2400], ["Vanity and top", "Materials", 1500],
      ["Drywall", "Subcontractor", 1400], ["Paint and fixtures", "Materials", 1200], ["Crew labor", "Labor", 7000], ["Permit", "Other", 350],
    ], "Priced from the Okafor and Patel bathrooms.", false, stamp(0, 2)),
    est("demo-est-2", "Composite deck, 14 by 20, for the Bell family", "Exterior", 27500, [
      ["Composite decking and rail", "Materials", 9800], ["Framing and concrete", "Materials", 2300], ["Crew labor", "Labor", 7800],
      ["Lighting and extras", "Materials", 1300], ["Permit", "Other", 250],
    ], "Close to the Whitfield deck. Check decking prices before sending.", false, stamp(1, 18)),
    est("demo-est-3", "Basement finish for the Alvarez family", "Remodel", 56000, [
      ["Framing", "Materials", 6000], ["Drywall", "Subcontractor", 4200], ["Flooring", "Materials", 4600], ["Electrical", "Subcontractor", 4000],
      ["Plumbing", "Subcontractor", 2900], ["Heating and cooling", "Subcontractor", 2400], ["Paint and trim", "Materials", 2600],
      ["Lighting", "Materials", 1300], ["Permits", "Other", 600], ["Crew labor", "Labor", 17500],
    ], "Same scope as the Reyes basement.", false, stamp(2, 9)),
  ];
  const templates = [
    est("demo-tmpl-1", "Kitchen remodel", "Remodel", 0, [
      ["Cabinets", "Materials", 0], ["Countertops", "Materials", 0], ["Electrical", "Subcontractor", 0], ["Plumbing", "Subcontractor", 0],
      ["Flooring", "Materials", 0], ["Crew labor", "Labor", 0],
    ], "Fill in amounts from the supplier quotes.", true, stamp(3, 1)),
    est("demo-tmpl-2", "Deck build", "Exterior", 0, [
      ["Decking and rail", "Materials", 0], ["Framing and concrete", "Materials", 0], ["Crew labor", "Labor", 0], ["Permit", "Other", 0],
    ], "", true, stamp(3, 1)),
  ];
  return { estimates, templates };
}

// ─── Public: the whole demo business ─────────────────────────────────────────

// today: a Date, or "YYYY-MM-DD" text (read as a local date, never new Date(string)); anything else throws. Returns everything the screens read, in the live shapes:
//   jobSummaries, autoMatched, suggested, untagged, tagged, overhead, dismissed, vendorRules,
//   estimates, templates (saved quotes and templates), totals, businessName, today ("YYYY-MM-DD").
// totals are dollars: { revenue, costs, profit, marginPct (string), overhead, outstanding }.
export function buildDemoData(today) {
  let t;
  if (today instanceof Date && !Number.isNaN(today.getTime())) t = today;
  else if (typeof today === "string" && /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(today)) {
    t = parseYMD(today);
    if (localYMD(t) !== today) t = null; // e.g. 2026-02-31
  }
  if (!t) throw new Error("buildDemoData needs today as a Date or a YYYY-MM-DD string");
  const jobSummaries = buildJobs(t);
  const jobIdOf = {};
  JOB_SPECS.forEach((s, i) => { jobIdOf[s.key] = jobSummaries[i].id; });
  const inbox = buildInbox(t, jobIdOf);
  const { estimates, templates } = buildEstimates(t);

  const sumC = (list, pick) => list.reduce((s, x) => s + cents(pick(x)), 0);
  const revenueC = sumC(jobSummaries, (j) => j.revenue);
  const costC = sumC(jobSummaries, (j) => j.costs);
  const margin = marginPercent(revenueC - costC, revenueC);
  return {
    businessName: DEMO_BUSINESS_NAME,
    today: localYMD(t),
    jobSummaries,
    ...inbox,
    vendorRules: buildVendorRules(),
    estimates,
    templates,
    totals: {
      revenue: dollars(revenueC), costs: dollars(costC), profit: dollars(revenueC - costC),
      marginPct: margin === null ? "0.0" : margin.toFixed(1),
      overhead: dollars(sumC(inbox.overhead, (x) => x.amount)),
      outstanding: dollars(sumC(jobSummaries, (j) => j.outstanding)),
    },
  };
}

// ─── Pre-written AI Analyst answers ──────────────────────────────────────────

const LAST_MONTH_QUESTION = "How did last month go?";
const MISSING_LABOR_QUESTION = "Which jobs are missing labor?";

const pct = (v) => `${v.toFixed(1)}%`;
const list = (names) => (names.length <= 1 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`);
const plural = (n, one, many) => (n === 1 ? one : many);

// Group jobs (dollar fields) into rows with exact cents, ranked later by the caller.
function groupRows(jobs, keyOf) {
  const map = new Map();
  jobs.forEach((j) => {
    const k = keyOf(j);
    const r = map.get(k) || { key: k, revenueC: 0, costsC: 0, jobs: 0 };
    r.revenueC += cents(j.revenue); r.costsC += cents(j.costs); r.jobs += 1;
    map.set(k, r);
  });
  return [...map.values()].map((r) => ({ ...r, profitC: r.revenueC - r.costsC, margin: marginPercent(r.revenueC - r.costsC, r.revenueC) }));
}

// data: the result of buildDemoData. Returns [{ question, answer, figures }]. Every number in an answer is
// worked out here from data.jobSummaries, so the answers always match the screens. figures holds the numbers
// used (dollars) so tests can check them. The first four questions are the ones the chat suggests.
export function demoAnswers(data) {
  const todayDate = parseYMD(data.today);
  const jobs = data.jobSummaries;
  const pending = [...data.suggested, ...data.untagged];
  const status = new Map(jobs.map((j) => [j.id, jobCompleteness(j, pending)]));
  const isComplete = (j) => status.get(j.id).status === "complete";
  const incompleteNames = (subset) => subset.filter((j) => !isComplete(j)).map((j) => j.name);
  const completeJobs = jobs.filter(isComplete);
  const finishedComplete = completeJobs.filter((j) => j.status === "Complete"); // in-progress jobs are not ranked
  const finishedJobs = jobs.filter((j) => j.status === "Complete");
  const inProgressNote = jobs.some((j) => j.status !== "Complete") ? " Jobs still in progress are not counted." : "";
  const leftOutLine = (subset) => {
    const names = incompleteNames(subset);
    return names.length ? ` I left out ${list(names)} because ${plural(names.length, "its", "their")} numbers are not complete yet.` : "";
  };
  const answers = [];

  // 1. Top jobs this year: finished jobs with complete numbers whose final invoice is dated this year.
  // With fewer than 3 such jobs the answer looks at the past 12 months instead and says so. The "left out"
  // list covers the same period as the ranked jobs.
  {
    const year = String(todayDate.getFullYear());
    const yearAgo = demoDate(todayDate, 12, todayDate.getDate());
    const finishedJobs = jobs.filter((j) => j.status === "Complete");
    const qualifies = (j) => isComplete(j) && cents(j.profit) > 0;
    const thisYear = (j) => j.lastDate.slice(0, 4) === year;
    const pastYear = (j) => j.lastDate >= yearAgo;
    let inPeriod = thisYear;
    let period = "so far this year";
    if (finishedJobs.filter((j) => qualifies(j) && thisYear(j)).length < 3) { inPeriod = pastYear; period = "over the past 12 months"; }
    const pool = finishedJobs.filter((j) => qualifies(j) && inPeriod(j));
    const ranked = pool.sort((a, b) => cents(b.profit) - cents(a.profit) || a.name.localeCompare(b.name)).slice(0, 3);
    const rows = ranked.map((j) => ({ id: j.id, name: j.name, revenue: j.revenue, profit: j.profit, marginPct: j.marginPct }));
    const one = (r) => `${r.name}, ${formatMoney(r.profit)} profit on ${formatMoney(r.revenue)} (${r.marginPct}% margin)`;
    const fallbackNote = period === "over the past 12 months" ? "I looked at the past 12 months because fewer than three jobs have finished so far this year. " : "";
    const body = rows.length === 0
      ? `${fallbackNote}No finished job with complete numbers has made a profit ${period}.`
      : rows.length === 1
        ? `${fallbackNote}Your most profitable finished job ${period} is ${one(rows[0])}.`
        : `${fallbackNote}Your most profitable finished jobs ${period} are ${rows.map((r, i) => `${i + 1}. ${one(r)}`).join("; ")}.`;
    const notDone = finishedJobs.filter((j) => !isComplete(j) && inPeriod(j)).map((j) => j.name);
    answers.push({
      question: AI_SUGGESTIONS[0],
      answer: body + (notDone.length ? ` I left out ${list(notDone)} because ${plural(notDone.length, "its", "their")} numbers are not complete yet.` : ""),
      figures: { period, jobs: rows, leftOut: notDone },
    });
  }

  // 2. Best clients (complete jobs only, all the history in the demo)
  {
    const rows = groupRows(finishedComplete, (j) => j.clientName).filter((r) => r.profitC > 0)
      .sort((a, b) => b.profitC - a.profitC || a.key.localeCompare(b.key));
    const top = rows.slice(0, 3);
    const worst = groupRows(finishedComplete, (j) => j.clientName).sort((a, b) => a.profitC - b.profitC || a.key.localeCompare(b.key))[0];
    const fig = (r) => ({ client: r.key, jobs: r.jobs, revenue: dollars(r.revenueC), profit: dollars(r.profitC), margin: r.margin });
    const topText = top.map((r, i) => `${i + 1}. ${r.key}, ${formatMoney(dollars(r.profitC))} profit on ${formatMoney(dollars(r.revenueC))} (${pct(r.margin)} margin, ${r.jobs} ${plural(r.jobs, "job", "jobs")})`).join("; ");
    const worstText = worst && worst.profitC < 0
      ? ` Your weakest client is ${worst.key}: you lost ${formatMoney(Math.abs(dollars(worst.profitC)))} there.`
      : "";
    answers.push({
      question: AI_SUGGESTIONS[1],
      answer: `Your most profitable clients, by profit, are ${topText}.${worstText}${leftOutLine(finishedJobs)}${inProgressNote}`,
      figures: { clients: top.map(fig), worst: worst ? fig(worst) : null, leftOut: incompleteNames(finishedJobs) },
    });
  }

  // 3. Kinds of work, by margin (complete jobs only)
  {
    const rows = groupRows(finishedComplete, (j) => j.type).filter((r) => r.margin !== null)
      .sort((a, b) => b.margin - a.margin || a.key.localeCompare(b.key));
    const text = rows.map((r) => `${r.key} at ${pct(r.margin)} (${formatMoney(dollars(r.profitC))} profit across ${r.jobs} ${plural(r.jobs, "job", "jobs")})`).join(", ");
    answers.push({
      question: AI_SUGGESTIONS[2],
      answer: `${rows[0].key} work pays off best for you. By margin: ${text}.${leftOutLine(finishedJobs)}${inProgressNote}`,
      figures: { types: rows.map((r) => ({ type: r.key, jobs: r.jobs, revenue: dollars(r.revenueC), profit: dollars(r.profitC), margin: r.margin })), leftOut: incompleteNames(finishedJobs) },
    });
  }

  // 4. Where to improve margins
  {
    const weakest = completeJobs.filter((j) => j.status === "Complete" && cents(j.revenue) > 0)
      .map((j) => ({ j, margin: marginPercent(cents(j.profit), cents(j.revenue)) }))
      .sort((a, b) => a.margin - b.margin || a.j.name.localeCompare(b.j.name)).slice(0, 2);
    const vendorC = {};
    jobs.forEach((j) => Object.entries(j.costByVendor).forEach(([v, a]) => { vendorC[v] = (vendorC[v] || 0) + cents(a); }));
    const materialC = Object.values(vendorC).reduce((s, c) => s + c, 0);
    const [topVendor, topVendorC] = Object.entries(vendorC).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0];
    const unassigned = [...data.suggested, ...data.untagged];
    const unassignedC = unassigned.reduce((s, x) => s + cents(x.amount), 0);
    const weakText = weakest.map(({ j, margin }) => `${j.name} (${margin < 0 ? `lost ${formatMoney(Math.abs(j.profit))}` : `${pct(margin)} margin`})`).join(" and ");
    answers.push({
      question: AI_SUGGESTIONS[3],
      answer: `Start with your weakest finished jobs: ${weakText}. Your biggest supplier is ${topVendor} at ${formatMoney(dollars(topVendorC))}, ${pct(marginPercent(topVendorC, materialC))} of what you spent on materials, so that is the first price to check. `
        + `Also, ${unassigned.length} ${plural(unassigned.length, "expense", "expenses")} worth ${formatMoney(dollars(unassignedC))} ${plural(unassigned.length, "is", "are")} not on any job yet. Assigning them will sharpen every margin.`,
      figures: {
        weakest: weakest.map(({ j, margin }) => ({ id: j.id, name: j.name, profit: j.profit, margin })),
        topVendor: { vendor: topVendor, amount: dollars(topVendorC), shareOfMaterials: marginPercent(topVendorC, materialC) },
        unassigned: { count: unassigned.length, amount: dollars(unassignedC) },
      },
    });
  }

  // 5. Last month
  {
    const lm = new Date(todayDate.getFullYear(), todayDate.getMonth() - 1, 1);
    const ym = `${lm.getFullYear()}-${pad(lm.getMonth() + 1, 2)}`;
    const row = monthlyTotalsForAI(jobs).find((r) => r.month === ym) || { month: ym, revenue: 0, costs: 0, profit: 0 };
    const margin = marginPercent(cents(row.profit), cents(row.revenue));
    const activeIncomplete = jobs.filter((j) => !isComplete(j) && jobEvents(j).some((e) => e.ym === ym)).map((j) => j.name);
    const label = `${MONTHS[lm.getMonth()]} ${lm.getFullYear()}`;
    const result = cents(row.profit) >= 0 ? `leaving ${formatMoney(row.profit)} profit` : `a loss of ${formatMoney(Math.abs(row.profit))}`;
    answers.push({
      question: LAST_MONTH_QUESTION,
      answer: `In ${label} you billed ${formatMoney(row.revenue)} and spent ${formatMoney(row.costs)} on jobs, ${result}${margin === null ? "" : ` (${pct(margin)} margin)`}.`
        + (activeIncomplete.length ? ` Treat that as a rough number: ${list(activeIncomplete)} ${plural(activeIncomplete.length, "has", "have")} gaps in the numbers.` : ""),
      figures: { month: ym, revenue: row.revenue, costs: row.costs, profit: row.profit, margin, incomplete: activeIncomplete },
    });
  }

  // 6. Missing labor
  {
    const noLabor = jobs.filter((j) => status.get(j.id).flags.noLabor && cents(j.costs) > 0);
    const others = jobs.filter((j) => !isComplete(j) && !noLabor.includes(j));
    const nlText = noLabor.map((j) => `${j.name} (${formatMoney(j.costs)} of costs and ${j.marginPct}% margin on paper)`).join("; ");
    const otherText = others.map((j) => `${j.name} (${status.get(j.id).reasons.join("; ").toLowerCase()})`).join("; ");
    answers.push({
      question: MISSING_LABOR_QUESTION,
      answer: (noLabor.length
        ? `${noLabor.length === 1 ? "One job has" : `${noLabor.length} jobs have`} costs but no labor recorded: ${nlText}. That margin is too good to trust until the crew's time is added.`
        : "Every job with costs has labor recorded.")
        + (others.length ? ` Other jobs with gaps in the numbers: ${otherText}.` : ""),
      figures: { noLabor: noLabor.map((j) => ({ id: j.id, name: j.name, costs: j.costs, marginPct: j.marginPct })), others: others.map((j) => j.id) },
    });
  }

  return answers;
}
